import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, BarChart, Bar, Cell,
} from 'recharts';
import * as adminService from '../../services/adminService';
import './AdminChurnPage.css';

type TabKey = 'churn' | 'cancellation' | 'retention' | 'trial' | 'referral';

interface ChurnPoint {
  month: string;
  startCount: number;
  churned: number;
  churnRate: number;
}

interface CancellationReason {
  reason: string;
  count: number;
}

interface CohortRow {
  cohortMonth: string;
  monthsSince: number;
  retentionPct: number;
}

interface TrialFunnel {
  registered: number;
  trialed: number;
  converted: number;
}

interface ReferralFunnel {
  totalReferrals: number;
  signedUp: number;
  subscribed: number;
}

// Group cohort data by cohortMonth
interface CohortGrouped {
  cohortMonth: string;
  retentions: Record<number, number>;
}

const REASON_LABELS: Record<string, string> = {
  too_expensive: 'Too Expensive',
  missing_features: 'Missing Features',
  switched_service: 'Switched Service',
  unused: 'Unused',
  other: 'Other',
};

const AdminChurnPage: React.FC = () => {
  const { t } = useTranslation('admin');

  const [activeTab, setActiveTab] = useState<TabKey>('churn');

  // Churn Rate state
  const [churnMonths, setChurnMonths] = useState(12);
  const [churnData, setChurnData] = useState<ChurnPoint[]>([]);
  const [churnLoading, setChurnLoading] = useState(false);

  // Cancellation state
  const [cancellationData, setCancellationData] = useState<CancellationReason[]>([]);
  const [cancellationLoading, setCancellationLoading] = useState(false);

  // Retention state
  const [retentionMonths, setRetentionMonths] = useState(6);
  const [retentionData, setRetentionData] = useState<CohortGrouped[]>([]);
  const [retentionMaxMonth, setRetentionMaxMonth] = useState(0);
  const [retentionLoading, setRetentionLoading] = useState(false);

  // Trial funnel state
  const [trialData, setTrialData] = useState<TrialFunnel | null>(null);
  const [trialLoading, setTrialLoading] = useState(false);

  // Referral funnel state
  const [referralData, setReferralData] = useState<ReferralFunnel | null>(null);
  const [referralLoading, setReferralLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // Fetch churn data
  const fetchChurn = useCallback(async () => {
    setChurnLoading(true);
    setError(null);
    try {
      const res = await adminService.getChurnRateTrend(churnMonths);
      setChurnData(res.data || []);
    } catch {
      setError(t('churn.fetchError', 'Failed to load churn data'));
    } finally {
      setChurnLoading(false);
    }
  }, [churnMonths, t]);

  // Fetch cancellation data
  const fetchCancellation = useCallback(async () => {
    setCancellationLoading(true);
    try {
      const res = await adminService.getCancellationReasons();
      const sorted = (res.data || []).sort((a: CancellationReason, b: CancellationReason) => b.count - a.count);
      setCancellationData(sorted);
    } catch {
      setError(t('churn.fetchError', 'Failed to load cancellation data'));
    } finally {
      setCancellationLoading(false);
    }
  }, [t]);

  // Fetch retention cohort
  const fetchRetention = useCallback(async () => {
    setRetentionLoading(true);
    try {
      const res = await adminService.getRetentionCohort(retentionMonths);
      const raw: CohortRow[] = res.data || [];

      // Group by cohortMonth
      const map = new Map<string, Record<number, number>>();
      let maxM = 0;
      raw.forEach((row) => {
        if (!map.has(row.cohortMonth)) map.set(row.cohortMonth, {});
        map.get(row.cohortMonth)![row.monthsSince] = row.retentionPct;
        if (row.monthsSince > maxM) maxM = row.monthsSince;
      });

      const grouped: CohortGrouped[] = Array.from(map.entries()).map(([cohortMonth, retentions]) => ({
        cohortMonth,
        retentions,
      }));

      setRetentionData(grouped);
      setRetentionMaxMonth(maxM);
    } catch {
      setError(t('churn.fetchError', 'Failed to load retention data'));
    } finally {
      setRetentionLoading(false);
    }
  }, [retentionMonths, t]);

  // Fetch trial funnel
  const fetchTrial = useCallback(async () => {
    setTrialLoading(true);
    try {
      const res = await adminService.getTrialFunnel();
      setTrialData(res);
    } catch {
      setError(t('churn.fetchError', 'Failed to load trial funnel'));
    } finally {
      setTrialLoading(false);
    }
  }, [t]);

  // Fetch referral funnel
  const fetchReferral = useCallback(async () => {
    setReferralLoading(true);
    try {
      const res = await adminService.getReferralFunnel();
      setReferralData(res);
    } catch {
      setError(t('churn.fetchError', 'Failed to load referral funnel'));
    } finally {
      setReferralLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (activeTab === 'churn') fetchChurn();
    else if (activeTab === 'cancellation') fetchCancellation();
    else if (activeTab === 'retention') fetchRetention();
    else if (activeTab === 'trial') fetchTrial();
    else if (activeTab === 'referral') fetchReferral();
  }, [activeTab, fetchChurn, fetchCancellation, fetchRetention, fetchTrial, fetchReferral]);

  const getHeatmapColor = (pct: number): string => {
    const hue = pct * 1.2; // 0 = red (0deg), 100 = green (120deg)
    return `hsl(${hue}, 70%, 50%)`;
  };

  const formatCohortMonth = (m: string): string => {
    const d = new Date(m + '-01');
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const renderSpinner = () => (
    <div className="admin-churn-loading">
      <div className="admin-churn-spinner" />
    </div>
  );

  const renderFunnelBar = (
    stages: { label: string; count: number; color: string }[],
  ) => {
    const maxCount = Math.max(...stages.map((s) => s.count), 1);
    return (
      <div className="admin-churn-funnel">
        {stages.map((stage, i) => (
          <React.Fragment key={stage.label}>
            <div className="admin-churn-funnel-stage">
              <div className="admin-churn-funnel-label">{stage.label}</div>
              <div className="admin-churn-funnel-bar-wrap">
                <div
                  className="admin-churn-funnel-bar"
                  style={{
                    width: `${Math.max((stage.count / maxCount) * 100, 8)}%`,
                    background: stage.color,
                  }}
                >
                  <span className="admin-churn-funnel-count">{stage.count.toLocaleString()}</span>
                </div>
              </div>
            </div>
            {i < stages.length - 1 && (
              <div className="admin-churn-funnel-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>
                <span className="admin-churn-funnel-rate">
                  {stages[i + 1].count > 0 && stages[i].count > 0
                    ? `${((stages[i + 1].count / stages[i].count) * 100).toFixed(1)}%`
                    : '0%'}
                </span>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'churn', label: t('churn.tabChurn', 'Churn Rate') },
    { key: 'cancellation', label: t('churn.tabCancellation', 'Cancellation Reasons') },
    { key: 'retention', label: t('churn.tabRetention', 'Retention Cohort') },
    { key: 'trial', label: t('churn.tabTrial', 'Trial Funnel') },
    { key: 'referral', label: t('churn.tabReferral', 'Referral Funnel') },
  ];

  return (
    <div className="admin-churn-page">
      <h1 className="admin-churn-title">{t('churn.title', 'Churn & Retention')}</h1>

      {/* Tabs */}
      <div className="admin-churn-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`admin-churn-tab ${activeTab === tab.key ? 'admin-churn-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="admin-churn-error-banner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* Tab 1: Churn Rate Trend */}
      {activeTab === 'churn' && (
        <div className="admin-churn-card">
          <div className="admin-churn-card-header">
            <span className="admin-churn-card-title">{t('churn.churnTrend', 'Monthly Churn Rate Trend')}</span>
            <div className="admin-churn-month-selector">
              {[6, 12, 24].map((m) => (
                <button
                  key={m}
                  className={`admin-churn-month-btn ${churnMonths === m ? 'admin-churn-month-btn--active' : ''}`}
                  onClick={() => setChurnMonths(m)}
                >
                  {m}m
                </button>
              ))}
            </div>
          </div>
          {churnLoading ? renderSpinner() : churnData.length === 0 ? (
            <p className="admin-churn-empty">{t('churn.noData', 'No churn data available yet.')}</p>
          ) : (
            <div className="admin-churn-chart-wrap">
              <ResponsiveContainer width="100%" height={360}>
                <LineChart data={churnData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      fontSize: 13,
                    }}
                    formatter={(value: unknown, name: unknown) => {
                      const v = Number(value);
                      if (name === 'churnRate') return [`${v.toFixed(2)}%`, 'Churn Rate'];
                      return [v, String(name)];
                    }}
                    labelFormatter={(label) => `Month: ${String(label)}`}
                  />
                  <ReferenceLine
                    y={5}
                    stroke="#94a3b8"
                    strokeDasharray="6 4"
                    label={{
                      value: '5% benchmark',
                      position: 'insideTopRight',
                      fill: '#94a3b8',
                      fontSize: 11,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="churnRate"
                    stroke="#dc2626"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: '#dc2626', stroke: '#fff', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Cancellation Reasons */}
      {activeTab === 'cancellation' && (
        <div className="admin-churn-card">
          <div className="admin-churn-card-header">
            <span className="admin-churn-card-title">{t('churn.cancellationReasons', 'Cancellation Reasons')}</span>
          </div>
          {cancellationLoading ? renderSpinner() : cancellationData.length === 0 ? (
            <p className="admin-churn-empty">{t('churn.noCancellationData', 'No cancellation data yet.')}</p>
          ) : (
            <div className="admin-churn-chart-wrap">
              <ResponsiveContainer width="100%" height={Math.max(200, cancellationData.length * 56)}>
                <BarChart
                  data={cancellationData}
                  layout="vertical"
                  margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis
                    type="category"
                    dataKey="reason"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    width={140}
                    tickFormatter={(v: string) => REASON_LABELS[v] || v}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 12,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      fontSize: 13,
                    }}
                    formatter={(value: unknown) => [Number(value), 'Count']}
                    labelFormatter={(label) => REASON_LABELS[String(label)] || String(label)}
                  />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={28}>
                    {cancellationData.map((_, index) => (
                      <Cell key={index} fill="#667eea" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Retention Cohort */}
      {activeTab === 'retention' && (
        <div className="admin-churn-card">
          <div className="admin-churn-card-header">
            <span className="admin-churn-card-title">{t('churn.retentionCohort', 'Retention Cohort')}</span>
            <div className="admin-churn-month-selector">
              {[3, 6, 12].map((m) => (
                <button
                  key={m}
                  className={`admin-churn-month-btn ${retentionMonths === m ? 'admin-churn-month-btn--active' : ''}`}
                  onClick={() => setRetentionMonths(m)}
                >
                  {m}m
                </button>
              ))}
            </div>
          </div>
          {retentionLoading ? renderSpinner() : retentionData.length === 0 ? (
            <p className="admin-churn-empty">{t('churn.noRetentionData', 'No retention data available yet.')}</p>
          ) : (
            <div className="admin-churn-heatmap-wrap">
              <table className="admin-churn-heatmap">
                <thead>
                  <tr>
                    <th className="admin-churn-heatmap-header">{t('churn.cohort', 'Cohort')}</th>
                    {Array.from({ length: retentionMaxMonth + 1 }).map((_, i) => (
                      <th key={i} className="admin-churn-heatmap-header">
                        {t('churn.monthN', 'M{{n}}', { n: i })}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {retentionData.map((row) => (
                    <tr key={row.cohortMonth}>
                      <td className="admin-churn-heatmap-label">{formatCohortMonth(row.cohortMonth)}</td>
                      {Array.from({ length: retentionMaxMonth + 1 }).map((_, i) => {
                        const pct = row.retentions[i];
                        const hasPct = pct !== undefined;
                        return (
                          <td key={i} className="admin-churn-heatmap-cell-td">
                            {hasPct ? (
                              <div
                                className="admin-churn-heatmap-cell"
                                style={{ background: getHeatmapColor(pct) }}
                                title={`${pct.toFixed(1)}%`}
                              >
                                {pct.toFixed(0)}%
                              </div>
                            ) : (
                              <div className="admin-churn-heatmap-cell admin-churn-heatmap-cell--empty">
                                --
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Trial Funnel */}
      {activeTab === 'trial' && (
        <div className="admin-churn-card">
          <div className="admin-churn-card-header">
            <span className="admin-churn-card-title">{t('churn.trialFunnel', 'Trial Funnel')}</span>
          </div>
          {trialLoading ? renderSpinner() : !trialData ? (
            <p className="admin-churn-empty">{t('churn.noTrialData', 'No trial funnel data available.')}</p>
          ) : (
            <>
              {renderFunnelBar([
                { label: t('churn.registered', 'Registered'), count: trialData.registered, color: '#93c5fd' },
                { label: t('churn.trialed', 'Trialed'), count: trialData.trialed, color: '#667eea' },
                { label: t('churn.converted', 'Converted'), count: trialData.converted, color: '#4338ca' },
              ])}
              <div className="admin-churn-funnel-summary">
                <p>
                  {t('churn.trialSummary',
                    '{{trialPct}}% of registered users started a trial, {{convertPct}}% converted to paid.',
                    {
                      trialPct: trialData.registered > 0
                        ? ((trialData.trialed / trialData.registered) * 100).toFixed(1)
                        : '0.0',
                      convertPct: trialData.trialed > 0
                        ? ((trialData.converted / trialData.trialed) * 100).toFixed(1)
                        : '0.0',
                    }
                  )}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab 5: Referral Funnel */}
      {activeTab === 'referral' && (
        <div className="admin-churn-card">
          <div className="admin-churn-card-header">
            <span className="admin-churn-card-title">{t('churn.referralFunnel', 'Referral Funnel')}</span>
          </div>
          {referralLoading ? renderSpinner() : !referralData ? (
            <p className="admin-churn-empty">{t('churn.noReferralData', 'No referral funnel data available.')}</p>
          ) : (
            <>
              {renderFunnelBar([
                { label: t('churn.referralCodes', 'Referral Codes'), count: referralData.totalReferrals, color: '#86efac' },
                { label: t('churn.signedUp', 'Signed Up'), count: referralData.signedUp, color: '#22c55e' },
                { label: t('churn.subscribed', 'Subscribed'), count: referralData.subscribed, color: '#15803d' },
              ])}
              <div className="admin-churn-funnel-summary">
                <p>
                  {t('churn.referralSummary',
                    '{{signupPct}}% of referrals signed up, {{subPct}}% subscribed.',
                    {
                      signupPct: referralData.totalReferrals > 0
                        ? ((referralData.signedUp / referralData.totalReferrals) * 100).toFixed(1)
                        : '0.0',
                      subPct: referralData.signedUp > 0
                        ? ((referralData.subscribed / referralData.signedUp) * 100).toFixed(1)
                        : '0.0',
                    }
                  )}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminChurnPage;
